/**
 * L'AFFICHE D'UNE SOIRÉE — le modèle pur du recadrage (22/10/2026, contrat
 * `soirees-v1` §9).
 *
 * Le navigateur recadre au format paysage de Shotgun, 16:9, et n'envoie que
 * l'image recadrée ; Gestion la revalide et la prépare (affiche nette, fond
 * flouté, teinte), la billetterie la range. Ici, tout ce qui se calcule sans
 * navigateur : le cadre (déplacer, agrandir, rester dans l'image), la taille
 * de sortie, les refus dits AVANT l'envoi (format, image trop petite, zone
 * trop étroite), la teinte dominante et le fond flouté de l'aperçu — ceux du
 * serveur les remplacent dès que l'affiche est posée.
 *
 * Le dessin sur `canvas` vit à part (`affiche-navigateur.ts`) : ce module se
 * teste hors navigateur (tests/billetterie-soirees-web.test.ts).
 */

import { UUID_RX, EMPREINTE_RX } from './soirees-maison-model.js'

/** Les visuels Shotgun mesurés sont à 1080 × 608 : du 16:9. */
export const RATIO_AFFICHE = 16 / 9
/** Gestion refuse en dessous (`BILLETTERIE_AFFICHE_PETITE`, §9.5) : l'écran le dit avant. */
export const LARGEUR_MIN_AFFICHE = 640
/** La sortie du navigateur : jamais plus, jamais agrandie. Le serveur réduit ensuite à 1 600. */
export const LARGEUR_MAX_SORTIE = 1920
/** L'envoi à Gestion (§9.5). */
export const POIDS_MAX_ENVOI = 8 * 1024 * 1024
/** Le décodage de Gestion (`limitInputPixels`) : une image plus lourde serait refusée là-bas. */
export const PIXELS_MAX = 40_000_000

export type TypeAffiche = 'image/jpeg' | 'image/png' | 'image/webp'
export const TYPES_AFFICHE: readonly TypeAffiche[] = ['image/jpeg', 'image/png', 'image/webp']
export const estTypeAffiche = (t: string): t is TypeAffiche => (TYPES_AFFICHE as readonly string[]).includes(t)

/* ═══════════════════════ le cadre ═══════════════════════ */

/** Un cadre en pixels de l'image source ; toujours `h = w × 9 / 16`. */
export interface Cadre { x: number; y: number; w: number; h: number }

/** Le plus grand cadre 16:9 que l'image contient. */
export function cadreMax(iw: number, ih: number): { w: number; h: number } {
  const w = Math.min(iw, ih * RATIO_AFFICHE)
  return { w, h: w / RATIO_AFFICHE }
}

/** Au départ : le plus grand cadre, centré — on ne perd que ce qui dépasse du 16:9. */
export function cadreInitial(iw: number, ih: number): Cadre {
  const m = cadreMax(iw, ih)
  return { x: (iw - m.w) / 2, y: (ih - m.h) / 2, w: m.w, h: m.h }
}

const borne = (v: number, min: number, max: number): number => Math.min(max, Math.max(min, v))

/** Le cadre ramené dans l'image, au bon rapport (comme `borne()` de l'assistant de fiche agent). */
export function bornerCadre(c: Cadre, iw: number, ih: number): Cadre {
  const m = cadreMax(iw, ih)
  const w = borne(c.w, Math.min(m.w, 16), m.w)
  const h = w / RATIO_AFFICHE
  return { x: borne(c.x, 0, iw - w), y: borne(c.y, 0, ih - h), w, h }
}

/** Le cadre déplacé de (dx, dy) pixels source, sans sortir de l'image. */
export function deplacerCadre(c: Cadre, dx: number, dy: number, iw: number, ih: number): Cadre {
  return bornerCadre({ ...c, x: c.x + dx, y: c.y + dy }, iw, ih)
}

/** La taille du cadre en pour cent du plus grand possible (le curseur « Taille du cadre »). */
export function tailleCadre(c: Cadre, iw: number, ih: number): number {
  return Math.round((c.w / cadreMax(iw, ih).w) * 100)
}

/** Agrandir ou réduire autour du CENTRE du cadre : ce qu'on regarde reste au milieu. */
export function redimensionnerCadre(c: Cadre, pourcent: number, iw: number, ih: number): Cadre {
  const w = cadreMax(iw, ih).w * borne(pourcent, 1, 100) / 100
  const h = w / RATIO_AFFICHE
  return bornerCadre({ x: c.x + c.w / 2 - w / 2, y: c.y + c.h / 2 - h / 2, w, h }, iw, ih)
}

/**
 * La taille de l'image envoyée : 16:9 EXACT (une largeur multiple de 16),
 * 1 920 × 1 080 au plus, jamais plus grande que la zone choisie.
 */
export function tailleSortie(c: Cadre): { largeur: number; hauteur: number } {
  const largeur = Math.floor(Math.min(c.w, LARGEUR_MAX_SORTIE) / 16) * 16
  return { largeur, hauteur: (largeur / 16) * 9 }
}

/* ═══════════════════════ les refus, dits avant ═══════════════════════ */

const px = (n: number): string => `${Math.floor(n).toLocaleString('fr-FR')} px`

export function refusType(type: string): string | null {
  return estTypeAffiche(type) ? null : 'L’affiche doit être une image JPEG, PNG ou WebP.'
}

/** L'image elle-même : trop lourde à décoder, ou trop petite pour un cadre 16:9 de 640 px. */
export function refusImage(iw: number, ih: number): string | null {
  if (!(iw > 0) || !(ih > 0)) return 'Cette image ne se lit pas : choisissez-en une autre.'
  if (iw * ih > PIXELS_MAX) return 'Cette image est trop grande (plus de 40 millions de pixels) : réduisez-la avant de la déposer.'
  const m = cadreMax(iw, ih)
  if (Math.floor(m.w) < LARGEUR_MIN_AFFICHE) {
    return `Cette image est trop petite : un cadre 16:9 n’y fait que ${px(m.w)} de large, il en faut ${px(LARGEUR_MIN_AFFICHE)} au moins.`
  }
  return null
}

/** La zone choisie : moins de 640 px de large, Gestion la refuserait. */
export function refusCadre(c: Cadre): string | null {
  if (Math.floor(c.w) >= LARGEUR_MIN_AFFICHE) return null
  return `La zone choisie fait ${px(c.w)} de large : il en faut ${px(LARGEUR_MIN_AFFICHE)} au moins. Agrandissez le cadre.`
}

/* ═══════════════════════ teinte et fond de l'aperçu ═══════════════════════ */

const hex2 = (n: number): string => Math.round(borne(n, 0, 255)).toString(16).padStart(2, '0')

/**
 * La teinte DOMINANTE, comme `stats().dominant` de sharp côté serveur : un
 * histogramme de 16 niveaux par canal (4 096 cases), la case la plus remplie,
 * rendue en son centre. Les pixels presque transparents ne comptent pas.
 * `null` sans pixel opaque : l'écran garde alors l'accent.
 */
export function teinteDominante(rgba: ArrayLike<number>): string | null {
  const cases = new Uint32Array(4096)
  for (let i = 0; i + 3 < rgba.length; i += 4) {
    if (rgba[i + 3]! < 128) continue
    cases[((rgba[i]! >> 4) << 8) | ((rgba[i + 1]! >> 4) << 4) | (rgba[i + 2]! >> 4)]! += 1
  }
  let meilleure = -1; let n = 0
  for (let k = 0; k < cases.length; k++) if (cases[k]! > n) { n = cases[k]!; meilleure = k }
  if (meilleure < 0) return null
  return `#${hex2(((meilleure >> 8) & 15) * 16 + 8)}${hex2(((meilleure >> 4) & 15) * 16 + 8)}${hex2((meilleure & 15) * 16 + 8)}`
}

/**
 * Le FOND de l'aperçu, préparé comme celui du serveur (§9.1 : flou, luminosité
 * 0,8, saturation 1,1) sur une petite image : trois passes de flou en boîte,
 * puis la lumière. Modifie `rgba` en place. Aucun filtre CSS : l'écran pose
 * l'image telle quelle, sous le même voile que le bandeau de la compta.
 */
export function preparerFond(rgba: Uint8ClampedArray, w: number, h: number, rayon = 3): Uint8ClampedArray {
  const tampon = new Float32Array(rgba.length)
  const passe = (horizontal: boolean) => {
    const long = horizontal ? w : h; const larg = horizontal ? h : w
    for (let a = 0; a < larg; a++) {
      for (let c = 0; c < 4; c++) {
        let somme = 0
        const idx = (b: number) => ((horizontal ? a * w + b : b * w + a) * 4) + c
        for (let b = -rayon; b <= rayon; b++) somme += rgba[idx(borne(b, 0, long - 1))]!
        for (let b = 0; b < long; b++) {
          tampon[idx(b)] = somme / (2 * rayon + 1)
          somme += rgba[idx(borne(b + rayon + 1, 0, long - 1))]! - rgba[idx(borne(b - rayon, 0, long - 1))]!
        }
      }
    }
    for (let i = 0; i < rgba.length; i++) rgba[i] = tampon[i]!
  }
  for (let k = 0; k < 3; k++) { passe(true); passe(false) }
  for (let i = 0; i + 3 < rgba.length; i += 4) {
    const r = rgba[i]! * 0.8; const g = rgba[i + 1]! * 0.8; const b = rgba[i + 2]! * 0.8
    const gris = 0.2126 * r + 0.7152 * g + 0.0722 * b
    rgba[i] = gris + (r - gris) * 1.1
    rgba[i + 1] = gris + (g - gris) * 1.1
    rgba[i + 2] = gris + (b - gris) * 1.1
    rgba[i + 3] = 255
  }
  return rgba
}

/* ═══════════════════════ l'affiche du brouillon ═══════════════════════ */

/**
 * L'affiche recadrée, gardée dans le brouillon de l'assistant (data URL : elle
 * survit au rechargement de l'onglet). `fond_url` est l'aperçu flouté ; la
 * teinte, celle de l'aperçu — le serveur calculera la sienne.
 */
export interface AfficheBrouillon {
  data_url: string
  fond_url: string
  type: TypeAffiche
  largeur: number
  hauteur: number
  teinte: string | null
}

const DATA_URL_RX = /^data:(image\/(?:jpeg|png|webp));base64,[A-Za-z0-9+/]+=*$/

export function lireAfficheBrouillon(v: unknown): AfficheBrouillon | null {
  if (typeof v !== 'object' || v === null || Array.isArray(v)) return null
  const o = v as Record<string, unknown>
  const d = typeof o['data_url'] === 'string' ? DATA_URL_RX.exec(o['data_url']) : null
  const f = typeof o['fond_url'] === 'string' && DATA_URL_RX.test(o['fond_url']) ? o['fond_url'] : null
  const l = o['largeur']; const h = o['hauteur']; const t = o['teinte']
  if (!d || !f || typeof l !== 'number' || typeof h !== 'number' || !Number.isInteger(l) || !Number.isInteger(h)) return null
  if (l < LARGEUR_MIN_AFFICHE || l > LARGEUR_MAX_SORTIE || l * 9 !== h * 16) return null
  if (t !== null && !(typeof t === 'string' && /^#[0-9a-f]{6}$/.test(t))) return null
  return { data_url: d[0], fond_url: f, type: d[1] as TypeAffiche, largeur: l, hauteur: h, teinte: t }
}

/** Les octets d'une data URL d'image, pour l'envoi binaire (§9.5) ; `null` si elle n'en est pas une. */
export function octetsDepuisDataUrl(dataUrl: string): { type: TypeAffiche; octets: Uint8Array } | null {
  const m = DATA_URL_RX.exec(dataUrl)
  if (!m) return null
  try {
    const brut = atob(dataUrl.slice(dataUrl.indexOf(',') + 1))
    const octets = new Uint8Array(brut.length)
    for (let i = 0; i < brut.length; i++) octets[i] = brut.charCodeAt(i)
    return { type: m[1] as TypeAffiche, octets }
  } catch { return null }
}

/**
 * Ce que compare la clé d'idempotence de l'envoi (`GardeCles`) : la version
 * lue et l'image elle-même. La même image renvoyée reprend la même clé (un
 * rejeu ne pose rien deux fois) ; une autre image, ou une autre version, en
 * tire une neuve. Empreinte FNV-1a de la data URL et sa longueur : pas de
 * `crypto.subtle`, absent hors HTTPS.
 */
export function signatureEnvoi(versionAffiche: number | null, dataUrl: string): string {
  let h = 0x811c9dc5
  for (let i = 0; i < dataUrl.length; i++) { h ^= dataUrl.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0 }
  return `${versionAffiche ?? 'aucune'}:${dataUrl.length}:${h.toString(16).padStart(8, '0')}`
}

/* ═══════════════════════ les adresses de Gestion ═══════════════════════ */

/**
 * L'adresse d'une image d'affiche, servie par GESTION (§9.5) — jamais par la
 * billetterie. `?v=` porte l'empreinte : la réponse est alors immuable et le
 * navigateur la garde ; une autre affiche a une autre adresse.
 */
export function urlAffiche(soireeId: string, variante: 'net' | 'fond', empreinte: string): string | null {
  if (!UUID_RX.test(soireeId) || !EMPREINTE_RX.test(empreinte)) return null
  return `/api/v1/billetterie/soirees/${soireeId.toLowerCase()}/affiche/${variante}?v=${empreinte}`
}
